import { useState, useMemo, useCallback, useEffect } from "react";
import { isAllBlanksFilled } from "../../../utils/quiz-parser.js";
function parseAnswerKey(key) {
  try {
    const parsed = JSON.parse(key);
    return Array.isArray(parsed) ? parsed.map((value) => String(value)) : [];
  } catch {
    return [];
  }
}
function toOption(source) {
  if (typeof source === "string") {
    const label2 = source.trim();
    return label2 ? { key: label2, label: label2 } : null;
  }
  if (!source || typeof source.answer !== "string") return null;
  const label = source.answer.trim();
  if (!label) return null;
  return { key: typeof source.key === "string" && source.key ? source.key : label, label };
}
function dedupeOptions(options) {
  const seen = /* @__PURE__ */ new Set();
  const result = [];
  options.forEach((option) => {
    const identity = `${option.key}\0${option.label}`;
    if (seen.has(identity)) return;
    seen.add(identity);
    result.push(option);
  });
  return result;
}
function extractProblemOptions(problem) {
  var _a;
  const options = [];
  const answerReg = /\{\{([^{}]*)}}/g;
  let match;
  while ((match = answerReg.exec(problem)) !== null) {
    const raw = (_a = match[1]) == null ? void 0 : _a.trim();
    if (!raw || /^`.+`$/.test(raw)) continue;
    const label = raw.includes(":") ? raw.slice(raw.indexOf(":") + 1).trim() : raw;
    if (label) options.push({ key: label, label });
  }
  return dedupeOptions(options);
}
function countDragTextBlanks(problem) {
  var _a;
  const answerReg = /\{\{([^{}]*)}}/g;
  let count = 0;
  let match;
  while ((match = answerReg.exec(problem)) !== null) {
    const raw = ((_a = match[1]) == null ? void 0 : _a.trim()) ?? "";
    if (/^`.+`$/.test(raw)) continue;
    count++;
  }
  return count;
}
function useDragTextQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  const [startTime] = useState(() => Date.now());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const blankCount = useMemo(() => countDragTextBlanks(quiz.problem), [quiz.problem]);
  const problemKey = useMemo(
    () => `${quizId}-${blankCount}-${quiz.problem.length}`,
    [quizId, blankCount, quiz.problem]
  );
  const isSolved = solveResult !== null && solveResult !== void 0;
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  const hasInitialAnswers = initialAnswersProp !== void 0;
  const initialAnswersKey = JSON.stringify(initialAnswersProp ?? []);
  const correctAnswersKey = JSON.stringify((solveResult == null ? void 0 : solveResult.correctAnswers) ?? []);
  const availableOptions = useMemo(() => {
    var _a;
    if (quiz.choices && Array.isArray(quiz.choices) && quiz.choices.length > 0) {
      return dedupeOptions(quiz.choices.map(toOption).filter((o) => Boolean(o)));
    }
    if (((_a = quiz.pairs) == null ? void 0 : _a.right) && Array.isArray(quiz.pairs.right) && quiz.pairs.right.length > 0) {
      return dedupeOptions(quiz.pairs.right.map(toOption).filter((o) => Boolean(o)));
    }
    if (quiz.answers && Array.isArray(quiz.answers) && quiz.answers.length > 0) {
      return dedupeOptions(quiz.answers.map(toOption).filter((o) => Boolean(o)));
    }
    return extractProblemOptions(quiz.problem);
  }, [quiz.answers, quiz.choices, quiz.pairs, quiz.problem]);
  const optionLabelByKey = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    availableOptions.forEach((o) => map.set(o.key, o.label));
    return map;
  }, [availableOptions]);
  const optionKeyByLabel = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    availableOptions.forEach((o) => {
      if (!map.has(o.label)) map.set(o.label, o.key);
    });
    return map;
  }, [availableOptions]);
  const normalizeValueToKey = useCallback((value) => {
    if (!value) return "";
    if (optionLabelByKey.has(value)) return value;
    return optionKeyByLabel.get(value) ?? value;
  }, [optionKeyByLabel, optionLabelByKey]);
  const resolveLabel = useCallback((value) => {
    if (!value) return "";
    return optionLabelByKey.get(value) ?? value;
  }, [optionLabelByKey]);
  const computedInitialAnswers = useMemo(() => {
    const initialAnswers = parseAnswerKey(initialAnswersKey);
    const correctAnswers = parseAnswerKey(correctAnswersKey);
    const base = hasInitialAnswers ? initialAnswers : isViewSolved && correctAnswers.length > 0 ? correctAnswers : new Array(blankCount).fill("");
    const normalized = new Array(blankCount).fill("").map((_, idx) => normalizeValueToKey(base[idx] ?? ""));
    return normalized;
  }, [blankCount, correctAnswersKey, hasInitialAnswers, initialAnswersKey, isViewSolved, normalizeValueToKey]);
  const [answers, setAnswers] = useState(computedInitialAnswers);
  const canSubmit = isAllBlanksFilled(answers);
  useEffect(() => {
    setAnswers((prev) => {
      if (prev.length === computedInitialAnswers.length && prev.every((v, i) => v === computedInitialAnswers[i])) {
        return prev;
      }
      return computedInitialAnswers;
    });
  }, [computedInitialAnswers]);
  useEffect(() => {
    onAnswerChange == null ? void 0 : onAnswerChange(answers);
  }, [answers, onAnswerChange]);
  const handleBlankClick = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setCurrentIndex(index);
  }, [isSolved, isViewSolved]);
  const handleClearBlank = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[index] = "";
      return newAnswers;
    });
  }, [isSolved, isViewSolved]);
  const handleSelectOption = useCallback((optionKey) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[currentIndex] = optionKey;
      const nextEmptyIndex = newAnswers.findIndex((a, i) => i > currentIndex && a === "");
      if (nextEmptyIndex !== -1) {
        setCurrentIndex(nextEmptyIndex);
      } else {
        const firstEmptyIndex = newAnswers.findIndex((a, i) => i !== currentIndex && a === "");
        if (firstEmptyIndex !== -1) {
          setCurrentIndex(firstEmptyIndex);
        }
      }
      return newAnswers;
    });
  }, [currentIndex, isSolved, isViewSolved]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, answers, quiz.vars, elapsedTime);
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime]);
  return {
    answers,
    currentIndex,
    blankCount,
    problemKey,
    availableOptions,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    resolveLabel,
    handleBlankClick,
    handleClearBlank,
    handleSelectOption,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  useDragTextQuiz
};
//# sourceMappingURL=use-drag-text-quiz.js.map
